import * as THREE from 'three';
import { fogRange } from '@/core/IsoCamera';

/**
 * Nachtverlichting.
 *
 * De boards laten een stad zien die donker is maar niet dood: warme ramen en natriumgeel
 * straatlicht snijden paden door het donker. Het zware werk doen emissieve vlakken (ramen,
 * neon) — die kosten niets. Echte lichtbronnen zijn schaars en gaan via de pool hieronder,
 * met afstands-culling en een hard maximum.
 */

/**
 * Lichtkleuren. Deze horen niet in `palette.ts`: dat bestand beschrijft oppervlakken, en
 * een lichtkleur is iets anders — hij vermenigvuldigt de oppervlaktekleur en moet daarom
 * licht zijn, ook 's nachts.
 */
const NIGHT_SKY = 0x8095ad;
const NIGHT_BOUNCE = 0x4a3a2e;
const MOONLIGHT = 0xb6c9dc;
const MOON_OFFSET = new THREE.Vector3(-24, 34, -18);
const SHADOW_FOCUS_SNAP = 4;

/** Meer dan dit aantal tegelijk actieve puntlichten kost meer dan het oplevert. */
const MAX_ACTIVE_LIGHTS = 12;

/** Buiten deze straal van de camera worden lampen uitgezet. */
const LIGHT_CULL_RADIUS = 34;

export interface NightRig {
  ambient: THREE.HemisphereLight;
  moon: THREE.DirectionalLight;
}

/** Zet de basisverlichting en de nachtmist neer. */
export function createNightLighting(scene: THREE.Scene): NightRig {
  scene.background = new THREE.Color(0x07080a);
  // Mist laat de stad in het donker wegvallen in plaats van bij een harde rand te stoppen.
  // Het bereik ligt rond de camera-afstand: bij een ortho-camera wordt mist vanaf de camera
  // gemeten, niet vanaf het focuspunt.
  const fog = fogRange();
  scene.fog = new THREE.Fog(0x07080a, fog.near, fog.far);

  // Genoeg ambient om silhouetten en materiaalzones te scheiden, niet genoeg om de nacht
  // weg te nemen. De kleuren zijn bewust lichter dan de paletkleuren zelf: een lichtbron
  // vermenigvuldigt de oppervlaktekleur, dus een donkere lichtkleur maakt alles dubbel
  // donker. De nacht komt uit de lage intensiteit, niet uit een donkere lichtkleur.
  const ambient = new THREE.HemisphereLight(NIGHT_SKY, NIGHT_BOUNCE, 0.85);
  ambient.name = 'NA_Light_Ambient';
  scene.add(ambient);

  const moon = new THREE.DirectionalLight(MOONLIGHT, 0.7);
  moon.name = 'NA_Light_Moon';
  moon.position.copy(MOON_OFFSET);
  moon.castShadow = true;
  moon.shadow.mapSize.set(2048, 2048);
  moon.shadow.bias = -0.0008;

  // Schaduwcamera strak om het speelgebied: hoe kleiner het volume, hoe scherper de schaduw.
  const shadow = moon.shadow.camera;
  shadow.left = -45;
  shadow.right = 45;
  shadow.top = 45;
  shadow.bottom = -45;
  shadow.near = 1;
  shadow.far = 120;
  shadow.updateProjectionMatrix();

  scene.add(moon);
  scene.add(moon.target);

  return { ambient, moon };
}

/**
 * Houdt het schaduwvolume rond de actuele camera-focus.
 *
 * De stad streamt per chunk; een statische directional-light shadow camera gaf daardoor
 * alleen het startgebied betrouwbare schaduwen. We snappen de target op het stadsgrid,
 * zodat de schaduwen bij langzaam camerawerk niet subpixel blijven kruipen.
 */
export function updateNightLighting(rig: NightRig, focus: THREE.Vector3): void {
  const x = Math.round(focus.x / SHADOW_FOCUS_SNAP) * SHADOW_FOCUS_SNAP;
  const z = Math.round(focus.z / SHADOW_FOCUS_SNAP) * SHADOW_FOCUS_SNAP;
  rig.moon.target.position.set(x, 0, z);
  rig.moon.position.set(x + MOON_OFFSET.x, MOON_OFFSET.y, z + MOON_OFFSET.z);
  rig.moon.target.updateMatrixWorld();
  rig.moon.updateMatrixWorld();
}

/**
 * Zacht licht dat met de speler meereist.
 *
 * Puur voor leesbaarheid: de stad is bewust donker en onvolledig verlicht, maar de speler
 * moet altijd te vinden zijn — ook in de zwarte stukken tussen twee lantaarns. Het licht is
 * koel en zwak, zodat het als maanlicht op de figuur leest en niet als een zaklamp.
 */
export const CHARACTER_LIGHT = {
  /**
   * Ophanghoogte boven de voeten. Lager dan de 3.6 m van eerder, zodat het lichaam in de kern
   * van de lichtplas valt in plaats van in de staart.
   */
  height: 2.4,
  /**
   * Bereik. Stond op 3.4 terwijl de lamp op 3.6 hing: de invloedsbol eindigde dús boven de
   * grond, en Luca's voeten kregen exact nul. Nu ruim genoeg voor het hele lichaam plus een
   * plas eromheen, zodat je ziet waar hij staat en niet alleen dat hij er is.
   */
  distance: 9,
  /** Zachter dan de 2.4 van eerder; die viel al binnen een meter vrijwel helemaal weg. */
  decay: 1.6,
} as const;

/**
 * Geen aparte renderlaag voor dit licht.
 *
 * Het lag voor de hand om de lamp op een eigen laag te zetten zodat hij alleen personages
 * raakt en niet het wegdek eronder — dat zou echt contrast geven. Maar zo werken layers in
 * three.js niet: `WebGLRenderer` toetst `light.layers` tegen de **camera**, niet per object.
 * Een lamp op een laag die de camera niet heeft, doet daardoor helemaal niets. Gemeten:
 * identieke beelden met de lamp op 0 en op volle sterkte.
 *
 * Selectief verlichten vraagt dus een eigen renderpass, en dat is deze leesbaarheidswinst
 * niet waard. De leesbaarheid komt van de algehele helderheid, niet van dit lampje.
 */

export function createCharacterLight(scene: THREE.Scene): THREE.PointLight {
  const light = new THREE.PointLight(MOONLIGHT, 1.15, CHARACTER_LIGHT.distance, CHARACTER_LIGHT.decay);
  light.name = 'NA_Light_Character';
  scene.add(light);
  return light;
}

/**
 * Pool van straatlantaarn- en neonlichten.
 *
 * Elke lamp is geregistreerd met een positie; per frame worden alleen de dichtstbijzijnde
 * lampen daadwerkelijk aan de scene toegevoegd. Zo kan de wijk honderden lichtpunten
 * hebben zonder dat de shader ze allemaal moet verwerken.
 */
export class LightPool {
  readonly #lights: THREE.PointLight[] = [];
  readonly #active = new Set<THREE.PointLight>();
  readonly #wanted = new Set<THREE.PointLight>();
  readonly #nearest: Array<{ light: THREE.PointLight; distance: number }> = [];
  readonly #group = new THREE.Group();

  constructor(scene: THREE.Scene) {
    this.#group.name = 'NA_LightPool';
    scene.add(this.#group);
  }

  /** Registreer een lichtpunt. Kleur uit het palet; `range` in meters. */
  add(position: THREE.Vector3, color: number, intensity: number, range: number): THREE.PointLight {
    const light = new THREE.PointLight(color, intensity, range, 2);
    light.position.copy(position);
    light.name = 'NA_Light_Point';
    this.#lights.push(light);
    return light;
  }

  remove(light: THREE.PointLight): void {
    const index = this.#lights.indexOf(light);
    if (index >= 0) this.#lights.splice(index, 1);
    this.#active.delete(light);
    this.#group.remove(light);
  }

  get registered(): number {
    return this.#lights.length;
  }

  get activeCount(): number {
    return this.#active.size;
  }

  /** Zet de dichtstbijzijnde lampen aan en de rest uit. Aanroepen bij het renderen. */
  update(focus: THREE.Vector3): void {
    this.#wanted.clear();
    this.#nearest.length = 0;

    const maxDistanceSq = LIGHT_CULL_RADIUS * LIGHT_CULL_RADIUS;
    for (const light of this.#lights) {
      const distance = light.position.distanceToSquared(focus);
      if (distance >= maxDistanceSq) continue;
      insertNearest(this.#nearest, light, distance);
    }

    for (const entry of this.#nearest) this.#wanted.add(entry.light);

    for (const light of this.#active) {
      if (!this.#wanted.has(light)) {
        this.#group.remove(light);
        this.#active.delete(light);
      }
    }

    for (const light of this.#wanted) {
      if (!this.#active.has(light)) {
        this.#group.add(light);
        this.#active.add(light);
      }
    }
  }

  clear(): void {
    this.#group.clear();
    this.#active.clear();
    this.#lights.length = 0;
  }
}

function insertNearest(
  nearest: Array<{ light: THREE.PointLight; distance: number }>,
  light: THREE.PointLight,
  distance: number,
): void {
  let index = nearest.length;
  while (index > 0 && nearest[index - 1]!.distance > distance) index--;
  if (index >= MAX_ACTIVE_LIGHTS) return;
  nearest.splice(index, 0, { light, distance });
  if (nearest.length > MAX_ACTIVE_LIGHTS) nearest.length = MAX_ACTIVE_LIGHTS;
}
